// Truck and site markers drawn on the Live Map
import React, { useState } from 'react';

import clsx from 'clsx';

import { AdvancedMarker } from '@/components/LiveMap/MapPrimitives';
import {
  formatSiteLabel,
  getCompassDirection,
  getOperatorInitials,
} from '@/components/LiveMap/liveMapFormat';
import {
  LiveMapJob,
  LiveMapOperator,
  LiveMapSite,
  OPERATOR_MARKER_COLORS,
  SiteRoleKind,
  TruckLocation,
  getIsLocationStale,
  getOperatorMarkerStyle,
  getSiteRoleKind,
} from '@/components/LiveMap/liveMapModel';
import { OperatorProfile } from '@/components/LiveMap/useLiveMapData';

// Diameter of the truck marker circle, used to offset its info window.
// react-app draws a 25px profile photo or a ~23px lettered circle.
export const TRUCK_MARKER_SIZE_PX = 26;

/**
 * Round operator avatar in react-app's operator-marker style: the profile photo,
 * or the first two letters of the nickname in black on white when there is no
 * photo or it fails to load. The ring color comes from the caller.
 * @param {object} props
 * @param {LiveMapOperator} props.operator
 * @param {string | null | undefined} props.avatarUrl
 * @param {string} [props.className] - Size, border and font-size classes.
 * @param {React.CSSProperties} [props.style]
 * @returns {React.JSX.Element}
 */
export function OperatorAvatar({
  operator,
  avatarUrl,
  className,
  style,
}: {
  operator: LiveMapOperator;
  avatarUrl: string | null | undefined;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [hasImageError, setHasImageError] = useState(false);
  const showPhoto = !!avatarUrl && !hasImageError;
  return (
    <div
      className={clsx(
        'flex items-center justify-center overflow-hidden rounded-full font-bold',
        className
      )}
      style={{
        background: OPERATOR_MARKER_COLORS.fill,
        color: OPERATOR_MARKER_COLORS.label,
        ...style,
      }}
    >
      {showPhoto ? (
        // eslint-disable-next-line @next/next/no-img-element -- small remote avatar inside a map marker
        <img
          src={avatarUrl}
          alt=""
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setHasImageError(true)}
        />
      ) : (
        <span className="leading-none">{getOperatorInitials(operator)}</span>
      )}
    </div>
  );
}

/**
 * A truck, drawn like react-app's operator marker (see getOperatorMarkerStyle):
 * profile photo or nickname letters on white, black ring that turns purple when
 * location sharing is off, half opacity when the operator isn't active. On top
 * of that, a notch on the ring points the way the truck is moving, and the truck
 * id tag carries the job color.
 * @param {object} props
 * @param {LiveMapOperator} props.operator
 * @param {TruckLocation} props.location
 * @param {OperatorProfile | undefined} props.profile - Avatar and tracking switch; undefined while loading.
 * @param {string} props.jobColor - Color of the truck's job, shown on its id tag.
 * @param {boolean} props.isSelected
 * @param {number} props.nowMs
 * @param {() => void} props.onClick
 * @returns {React.JSX.Element}
 */
export function TruckMarker({
  operator,
  location,
  profile,
  jobColor,
  isSelected,
  nowMs,
  onClick,
}: {
  operator: LiveMapOperator;
  location: TruckLocation;
  profile: OperatorProfile | undefined;
  jobColor: string;
  isSelected: boolean;
  nowMs: number;
  onClick: () => void;
}) {
  const { heading } = location;
  const style = getOperatorMarkerStyle({
    lastFixMs: location.latest.timestamp,
    isTracking: operator.isSimulated ? true : profile?.isTracking,
    nowMs,
  });
  const isFresh = !getIsLocationStale(location, nowMs);
  const isMoving = isFresh && location.latest.isMoving && heading !== null;
  const isTrackingOff =
    !operator.isSimulated && !!profile && profile.isTracking !== true;
  const title = [
    operator.truckDisplayId,
    operator.name,
    isMoving && `heading ${getCompassDirection(heading as number)}`,
    isTrackingOff && 'location sharing off',
    !isFresh && 'no signal for 5+ min',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <AdvancedMarker
      position={location.latest}
      zIndex={isSelected ? 1000 : style.isActive ? 200 : 100}
      title={title}
      onClick={onClick}
    >
      {/* Shift down by half its height so the circle's center sits on the position */}
      <div
        className="truck-marker relative"
        style={{
          width: TRUCK_MARKER_SIZE_PX,
          height: TRUCK_MARKER_SIZE_PX,
          transform: 'translateY(50%)',
          opacity: isSelected ? 1 : style.opacity,
        }}
      >
        {isSelected && (
          <span
            className="absolute -inset-1.5 animate-ping rounded-full"
            style={{ background: style.strokeColor, opacity: 0.35 }}
          />
        )}
        {isMoving && (
          <div
            className="absolute -inset-[8px]"
            style={{ transform: `rotate(${heading}deg)` }}
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 12 10"
              className="absolute top-0 left-1/2 h-2 w-2.5 -translate-x-1/2"
              style={{ fill: style.strokeColor }}
            >
              <path d="M6 0 L12 10 L0 10 Z" />
            </svg>
          </div>
        )}
        <OperatorAvatar
          operator={operator}
          avatarUrl={profile?.avatarUrl}
          className={clsx(
            'relative h-full w-full border-2 text-xs shadow-md',
            isSelected && 'outline-accent outline-2 outline-offset-2'
          )}
          style={{ borderColor: style.strokeColor }}
        />
        {!!operator.truckDisplayId && (
          <div
            className="bg-base-100/90 absolute top-1/2 left-full ml-1.5 -translate-y-1/2 rounded border-l-4 px-1 text-[10px] leading-4 font-bold whitespace-nowrap shadow"
            style={{ borderLeftColor: jobColor }}
          >
            {operator.truckDisplayId}
          </div>
        )}
      </div>
    </AdvancedMarker>
  );
}

export type SiteMarkerData = {
  site: LiveMapSite;
  // Each visible job using this site, with the site as that job sees it (letter, role)
  entries: Array<{ job: LiveMapJob; site: LiveMapSite }>;
  // Jobs from the last week using this site, shown as context on future days
  contextEntries: Array<{ job: LiveMapJob; site: LiveMapSite }>;
};

// What trucks do at a site across its jobs. Told apart by shape, not color, so
// the icon doesn't compete with the status colors (red is primary, purple is
// warning) and reads for colorblind dispatchers too.
export const SITE_ROLE_LABELS: Record<Exclude<SiteRoleKind, 'stop'>, string> = {
  load: 'Load site',
  dump: 'Dump site',
  both: 'Load and dump site',
};

// Shape colors: dark on the map and in the legend, white inside a colored tag
const SITE_ROLE_ICON_TONES = {
  dark: { shape: '#000000', halo: '#ffffff' },
  light: { shape: '#ffffff', halo: null },
} as const;

/**
 * Small icon for a site's role, or nothing for a middle stop. Trucks fill up at
 * a load site (filled circle) and empty out at a dump site (hollow ring); a
 * site that is both is half filled.
 * @param {object} props
 * @param {SiteRoleKind} props.roleKind
 * @param {'dark' | 'light'} [props.tone] - Dark on the map, light on a colored tag.
 * @param {string} [props.className] - Position classes.
 * @returns {React.JSX.Element | null}
 */
export function SiteRoleIcon({
  roleKind,
  tone = 'dark',
  className,
}: {
  roleKind: SiteRoleKind;
  tone?: keyof typeof SITE_ROLE_ICON_TONES;
  className?: string;
}) {
  if (roleKind === 'stop') return null;
  const label = SITE_ROLE_LABELS[roleKind];
  const { shape, halo } = SITE_ROLE_ICON_TONES[tone];
  return (
    <svg
      viewBox="0 0 12 12"
      className={clsx('site-role-icon inline-block size-3 shrink-0', className)}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {!!halo && <circle cx={6} cy={6} r={6} fill={halo} />}
      <circle
        cx={6}
        cy={6}
        r={4}
        fill={roleKind === 'load' ? shape : 'none'}
        stroke={shape}
        strokeWidth={1.75}
      />
      {/* Left half filled */}
      {roleKind === 'both' && <path d="M6 2 A4 4 0 0 0 6 10 Z" fill={shape} />}
    </svg>
  );
}

// react-app's site pin ("cleanMarker" in components/Map/Marker.js): a teardrop
// with its tip at (0, 0), drawn at scale 0.9 with a grey fill and black stroke,
// and the site letter centered in the round head.
const SITE_PIN_PATH =
  'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';
const SITE_PIN_SCALE = 0.9;
const SITE_PIN_VIEWBOX = { x: -11, y: -41, width: 22, height: 42 };
const SITE_PIN_COLORS = { fill: 'grey', stroke: '#000000', label: '#000000' };
// Height of the pin, used to offset the site info window
export const SITE_PIN_HEIGHT_PX = SITE_PIN_VIEWBOX.height * SITE_PIN_SCALE;

// Height of the colored site tag (label plus stem), used to offset the site info window
export const SITE_TAG_HEIGHT_PX = 30;
// Neutral tag color for a site shared by several selected jobs
const SHARED_SITE_COLOR = '#3d4451';

export type SiteMarkerVariant = 'pin' | 'tag';

/**
 * A job site. With no job selected it is drawn like react-app's site marker: a
 * grey pin labeled with the site's letter in the job (A = load site, then B,
 * C...). Once jobs are selected it becomes a tag in the job's color with the
 * letter and site name, plus how many trucks are on site. A site shared by
 * several jobs shows one letter per job ("A/A", "A/C"), summarizing past two
 * letters on pins and four on tags ("A/A+3").
 * @param {object} props
 * @param {SiteMarkerData} props.data
 * @param {SiteMarkerVariant} props.variant
 * @param {number} props.truckCount - Live trucks inside the geofence (tags only).
 * @param {boolean} props.isHighlighted - The selected truck just came from here.
 * @param {boolean} props.isSelected - Its info window is open.
 * @param {() => void} props.onClick
 * @returns {React.JSX.Element}
 */
export function SiteMarker({
  data,
  variant,
  truckCount,
  isHighlighted,
  isSelected,
  onClick,
}: {
  data: SiteMarkerData;
  variant: SiteMarkerVariant;
  truckCount: number;
  isHighlighted: boolean;
  isSelected: boolean;
  onClick: () => void;
}) {
  const { site, entries, contextEntries } = data;
  // Sites only used by last week's jobs (future days) are drawn faded
  const isContextOnly = !entries.length;
  const labelEntries = isContextOnly ? contextEntries : entries;
  // One letter per job, so a site several jobs share reads "A/A" or "A/C"
  const letters = labelEntries.map((entry) => entry.site.label);
  const roleKind = getSiteRoleKind(
    [...entries, ...contextEntries].map((entry) => entry.site.role)
  );
  return (
    <AdvancedMarker
      position={site}
      zIndex={isSelected || isHighlighted ? 900 : isContextOnly ? 40 : 50}
      title={[
        site.name,
        roleKind === 'stop' ? 'Stop' : SITE_ROLE_LABELS[roleKind],
        ...labelEntries.map((entry) => entry.job.name),
      ].join(' · ')}
      onClick={onClick}
    >
      {variant === 'tag' ? (
        <SiteTag
          data={data}
          roleKind={roleKind}
          label={formatSiteLabel(letters, 4)}
          truckCount={truckCount}
          isHighlighted={isHighlighted}
          isSelected={isSelected}
        />
      ) : (
        <SitePin
          roleKind={roleKind}
          isFaded={isContextOnly}
          label={formatSiteLabel(letters, 2)}
          isHighlighted={isHighlighted}
          isSelected={isSelected}
        />
      )}
    </AdvancedMarker>
  );
}

/**
 * react-app style grey site pin with the site letter.
 * @returns {React.JSX.Element}
 */
function SitePin({
  roleKind,
  isFaded,
  label,
  isHighlighted,
  isSelected,
}: {
  roleKind: SiteRoleKind;
  isFaded: boolean;
  label: string;
  isHighlighted: boolean;
  isSelected: boolean;
}) {
  const { x, y, width, height } = SITE_PIN_VIEWBOX;
  return (
    <div
      className="site-marker relative"
      style={{
        width: width * SITE_PIN_SCALE,
        height: SITE_PIN_HEIGHT_PX,
        opacity: isFaded && !isSelected ? 0.55 : 1,
      }}
    >
      <SiteRoleIcon
        roleKind={roleKind}
        className="absolute -top-0.5 -right-1.5 z-1"
      />
      {(isHighlighted || isSelected) && (
        // Halo around the pin head
        <span
          className={clsx(
            'absolute left-1/2 size-7 -translate-x-1/2 rounded-full',
            isHighlighted && 'animate-ping'
          )}
          style={{
            top: (-30 - y) * SITE_PIN_SCALE - 14,
            background: isHighlighted ? 'var(--color-info)' : '#0098cd',
            opacity: 0.45,
          }}
        />
      )}
      <svg
        className="relative drop-shadow-sm"
        width={width * SITE_PIN_SCALE}
        height={SITE_PIN_HEIGHT_PX}
        viewBox={`${x} ${y} ${width} ${height}`}
        aria-hidden="true"
      >
        <path
          d={SITE_PIN_PATH}
          fill={SITE_PIN_COLORS.fill}
          stroke={SITE_PIN_COLORS.stroke}
          strokeWidth={1.5 / SITE_PIN_SCALE}
        />
        <text
          x={0}
          y={-29}
          textAnchor="middle"
          dominantBaseline="central"
          fill={SITE_PIN_COLORS.label}
          fontWeight="bold"
          fontSize={
            (label.length === 1 ? 12 : label.length <= 3 ? 9 : 7) /
            SITE_PIN_SCALE
          }
        >
          {label}
        </text>
      </svg>
    </div>
  );
}

/**
 * Site tag in the job's color with letter, name and trucks on site, used while
 * jobs are selected so the dispatcher can read the sites at a glance.
 * @returns {React.JSX.Element}
 */
function SiteTag({
  data,
  roleKind,
  label,
  truckCount,
  isHighlighted,
  isSelected,
}: {
  data: SiteMarkerData;
  roleKind: SiteRoleKind;
  label: string;
  truckCount: number;
  isHighlighted: boolean;
  isSelected: boolean;
}) {
  const colors = Array.from(
    new Set(data.entries.map((entry) => entry.job.color))
  );
  const color = colors.length === 1 ? (colors[0] as string) : SHARED_SITE_COLOR;
  return (
    <div
      className="site-marker flex flex-col items-center"
      style={{ height: SITE_TAG_HEIGHT_PX }}
    >
      <div
        className={clsx(
          'flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs whitespace-nowrap text-white shadow-md',
          isHighlighted && 'ring-info animate-pulse ring-4',
          isSelected && !isHighlighted && 'ring-base-300 ring-2'
        )}
        style={{ background: color }}
      >
        <SiteRoleIcon roleKind={roleKind} tone="light" />
        <span className="font-bold">{label}</span>
        <span className="max-w-36 truncate">{data.site.name}</span>
        {truckCount > 0 && (
          <span
            className="badge badge-xs bg-base-100 text-base-content border-0 font-bold"
            title={`${truckCount} ${truckCount === 1 ? 'truck' : 'trucks'} on site`}
          >
            {truckCount}
          </span>
        )}
      </div>
      <div className="w-0.5 flex-1" style={{ background: color }} />
    </div>
  );
}
